import React, {
  useState,
  useRef,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import './DropdownSelect.css';

const normalizeOptions = (options = []) =>
  options.map((option, index) => {
    if (typeof option === 'string' || typeof option === 'number') {
      return {
        value: option,
        label: option,
        disabled: false,
        key: `option-${index}-${option}`,
      };
    }

    if (option && typeof option === 'object') {
      return {
        value:
          option.value ??
          option.id ??
          option.label ??
          `option-${index}-${Math.random().toString(36).slice(2)}`,
        label: option.label ?? option.value ?? '',
        disabled: Boolean(option.disabled),
        key: option.key ?? `option-${index}`,
      };
    }

    return {
      value: '',
      label: '',
      disabled: false,
      key: `option-${index}-empty`,
    };
  });

const buildSyntheticEvent = (name, id, value) => ({
  target: { name, id, value },
  currentTarget: { name, id, value },
  type: 'dropdown-select-change',
  nativeEvent: null,
});

const MENU_GAP = 4;
const VIEWPORT_MARGIN = 10;
const MENU_MAX_HEIGHT_DEFAULT = 280;

const DropdownSelect = ({
  id,
  name,
  value,
  options = [],
  placeholder = 'Select...',
  onChange,
  onValueChange,
  className = '',
  menuClassName = '',
  disabled = false,
  required = false,
  size = 'md',
  /** 'down' | 'up' | 'auto' - force menu direction; 'auto' uses viewport space */
  preferDirection = 'auto',
}) => {
  const normalizedOptions = useMemo(
    () => normalizeOptions(options),
    [options]
  );
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const fallbackIdRef = useRef(
    `dropdown-select-${Math.random().toString(36).slice(2)}`
  );
  const dropdownId = id || fallbackIdRef.current;
  const menuId = `${dropdownId}-menu`;

  const selectedIndex = normalizedOptions.findIndex(
    (option) => option.value === value
  );
  const selectedOption =
    selectedIndex >= 0 ? normalizedOptions[selectedIndex] : null;

  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    direction: 'down',
    maxHeight: null,
  });

  const estimateMenuHeight = useCallback(() => {
    const visibleItems = Math.min(normalizedOptions.length || 1, 6);
    return visibleItems * 44 + 16; // option height + padding
  }, [normalizedOptions.length]);

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const estimatedHeight = estimateMenuHeight();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const autoOpenUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
    const shouldOpenUp =
      preferDirection === 'up' ? true : preferDirection === 'down' ? false : autoOpenUp;

    const top = shouldOpenUp
      ? Math.max(VIEWPORT_MARGIN, rect.top - estimatedHeight - MENU_GAP)
      : Math.min(window.innerHeight - VIEWPORT_MARGIN, rect.bottom + MENU_GAP);

    const left = Math.min(
      Math.max(VIEWPORT_MARGIN, rect.left),
      Math.max(VIEWPORT_MARGIN, window.innerWidth - rect.width - VIEWPORT_MARGIN)
    );

    // Clamp maxHeight so menu doesn't overflow viewport (10px margin from edge)
    const availableDown = window.innerHeight - (rect.bottom + MENU_GAP) - VIEWPORT_MARGIN;
    const availableUp = (rect.top - MENU_GAP) - VIEWPORT_MARGIN;
    const clampedMaxHeight = Math.min(
      MENU_MAX_HEIGHT_DEFAULT,
      shouldOpenUp ? Math.max(80, availableUp) : Math.max(80, availableDown)
    );

    setMenuPosition({
      top,
      left,
      width: rect.width,
      direction: shouldOpenUp ? 'up' : 'down',
      maxHeight: clampedMaxHeight,
    });
  }, [estimateMenuHeight, preferDirection]);

  const openMenu = useCallback(() => {
    if (disabled || isOpen) return;
    updateMenuPosition();
    setIsOpen(true);
  }, [disabled, isOpen, updateMenuPosition]);

  const closeMenu = useCallback(() => {
    if (!isOpen) return;
    setIsOpen(false);
  }, [isOpen]);

  const toggleMenu = useCallback(() => {
    if (disabled) return;
    if (isOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  }, [disabled, isOpen, openMenu, closeMenu]);

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (event) => {
      if (
        !triggerRef.current?.contains(event.target) &&
        !menuRef.current?.contains(event.target)
      ) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isOpen, closeMenu]);

  useEffect(() => {
    if (!isOpen) return;

    updateMenuPosition();

    const handleResize = () => updateMenuPosition();
    const handleScroll = () => updateMenuPosition();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (isOpen) {
      setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }
  }, [isOpen, selectedIndex]);

  const emitChange = useCallback(
    (option) => {
      if (disabled || !option) return;

      if (onChange) {
        onChange(buildSyntheticEvent(name, dropdownId, option.value));
      }

      if (onValueChange) {
        onValueChange(option.value, option);
      }
    },
    [disabled, name, dropdownId, onChange, onValueChange]
  );

  const handleSelectOption = (option, index) => {
    if (option.disabled) return;
    closeMenu();
    setFocusedIndex(index);

    if (option.value !== value) {
      emitChange(option);
    }
  };

  const focusNextOption = (direction) => {
    if (!normalizedOptions.length) return;

    let nextIndex = focusedIndex;

    do {
      nextIndex =
        (nextIndex + direction + normalizedOptions.length) %
        normalizedOptions.length;
    } while (normalizedOptions[nextIndex]?.disabled);

    setFocusedIndex(nextIndex);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          openMenu();
        } else {
          focusNextOption(1);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!isOpen) {
          openMenu();
        } else {
          focusNextOption(-1);
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!isOpen) {
          openMenu();
        } else if (focusedIndex >= 0) {
          handleSelectOption(normalizedOptions[focusedIndex], focusedIndex);
        }
        break;
      case 'Escape':
        event.preventDefault();
        closeMenu();
        triggerRef.current?.focus();
        break;
      default:
        break;
    }
  };

  const sizeClass = size ? `dropdown-select--${size}` : '';
  const menuSizeClass = size ? `dropdown-select-menu--${size}` : '';

  const menu = (
    <ul
      id={menuId}
      ref={menuRef}
      role="listbox"
      className={[
        'dropdown-select-menu',
        menuClassName,
        menuSizeClass,
        `dropdown-select-menu--${menuPosition.direction}`,
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        top: menuPosition.top,
        left: menuPosition.left,
        minWidth: menuPosition.width,
        ...(menuPosition.maxHeight != null && { maxHeight: menuPosition.maxHeight }),
      }}
      aria-labelledby={dropdownId}
    >
      {normalizedOptions.map((option, index) => {
        const optionId = `${menuId}-option-${index}`;
        const isSelected = option.value === value;
        const isFocused = index === focusedIndex;

        return (
          <li
            key={option.key || optionId}
            id={optionId}
            role="option"
            aria-selected={isSelected}
            className={[
              'dropdown-select-option',
              isSelected ? 'is-selected' : '',
              isFocused ? 'is-focused' : '',
              option.disabled ? 'is-disabled' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleSelectOption(option, index)}
            onMouseEnter={() => setFocusedIndex(index)}
          >
            {option.label}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className={`dropdown-select ${sizeClass}`.trim()}>
      <button
        type="button"
        id={dropdownId}
        name={name}
        className={[
          'dropdown-select-trigger',
          className,
          isOpen ? 'is-open' : '',
          disabled ? 'is-disabled' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={toggleMenu}
        onKeyDown={handleKeyDown}
        ref={triggerRef}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={menuId}
        disabled={disabled}
      >
        <span
          className={[
            'dropdown-select-label',
            !selectedOption ? 'is-placeholder' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <span className="dropdown-select-arrow" aria-hidden="true" />
      </button>

      {name ? (
        <input
          type="hidden"
          name={name}
          value={value ?? ''}
          required={required}
        />
      ) : null}

      {isOpen ? ReactDOM.createPortal(menu, document.body) : null}
    </div>
  );
};

export default DropdownSelect;
